import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { ParticipantFormType } from "@shared";
import { participantValidationSchema, toNullable } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { UpdateProceedingParticipantRequest, proceedingParticipantsApi } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { formatTimestamp } from "@/common/utils/formatTimestamp";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useParticipantEdit(participantId: string, onClose: () => void) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const { data: participant, isLoading } = useQuery({
    queryKey: [QueryKey.ProceedingParticipants, participantId],
    queryFn: () => proceedingParticipantsApi.getProceedingParticipantById({ participantId }),
  });

  const updateParticipantMutation = useMutation({
    mutationFn: (request: UpdateProceedingParticipantRequest) =>
      proceedingParticipantsApi.updateProceedingParticipant({
        participantId,
        updateProceedingParticipantRequest: request,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ProceedingParticipants] });
      showSuccess(t("proceedingParticipants.edit.successfullyUpdated"));
      onClose();
    },
  });

  const validationSchema = useMemo(() => participantValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<ParticipantFormType>({
    initialValues: {
      type: participant?.type ?? null,
      name: participant?.name ?? "",
      street: participant?.street ?? "",
      houseNumber: participant?.houseNumber ?? "",
      country: participant?.country ?? "",
      zipCode: participant?.zipCode ?? "",
      city: participant?.city ?? "",
      note: participant?.note ?? null,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const request: UpdateProceedingParticipantRequest = {
        name: values.name.trim(),
        street: toNullable(values.street),
        houseNumber: toNullable(values.houseNumber),
        country: toNullable(values.country),
        zipCode: toNullable(values.zipCode),
        city: toNullable(values.city),
        note: toNullable(values.note),
      };

      await updateParticipantMutation.mutateAsync(request);
    },
  });

  const lastChangedText =
    participant?.lastChangedAtUtc != null
      ? t("proceedingParticipants.edit.lastChanged", {
          date: formatTimestamp(participant.lastChangedAtUtc, t, language),
          user: participant.lastChangedByUserCode,
        })
      : null;

  return { formik, isLoading, lastChangedText, participant };
}
